import React, { useImperativeHandle, useState } from 'react';
import useModal from '@app/component/overlays/modal/ModalRef';
import Modal from '@app/component/overlays/modal/Modal';
import AlertService from '@app/model/alert/service/AlertService';
import Utilities from '@util/Utilities';
import Tag from '@app/component/tag/Tag';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import useEvaluateMarketStateForSettingsHook from '@app/site/secured/dashboard/hook/EvaluateMarketStateForSettingsHook';
import CompanyHotelSettings from '@app/model/company_hotel/CompanyHotelSettings';
import _isEmpty from 'lodash/isEmpty';
import MarketStateDailySnapshot from '@app/model/market_state/MarketStateDailySnapshot';
import MarketStateDailyHotelSnapshot from '@app/model/market_state/MarketStateDailyHotelSnapshot';
import MarketStateSnapshotCalendarDayTableRow
	from '@app/site/secured/dashboard/fragment/__market-state/common/tooltip/MarketStateSnapshotCalendarDayTableRow';
import CalendarDay from '@app/state/calendar/CalendarDay';


const MarketStateDetailsModal: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	settings: CompanyHotelSettings,
	ref: any
}> = React.forwardRef(({ companyHotel, settings }, ref: React.Ref<any>) => {

	const modal = useModal();
	const [marketStateForDay, setMarketStateForDay] = useState<MarketStateDailySnapshot>();
	const [mainHotelMarketState, setMainHotelMarketState] = useState<MarketStateDailyHotelSnapshot>();
	const [calendarDay, setCalendarDay] = useState<CalendarDay>();
	const evaluator = useEvaluateMarketStateForSettingsHook(settings);

	useImperativeHandle(ref, () => {
		return {
			open: (alertId: number, marketStateId: number) => {
				if (!!companyHotel)
					AlertService.findAlertNotificationMarketState({
						hotelId: companyHotel!.hotel.id,
						alertId: alertId,
						marketStateId: marketStateId,
					}).then((marketStateForDay) => {
						if (!!marketStateForDay) {
							setMarketStateForDay(marketStateForDay);
							const mainHotelMarketState = !_isEmpty(marketStateForDay.hotelItems) ? marketStateForDay.hotelItems[marketStateForDay.mainHotelItemIndex] : undefined;
							setMainHotelMarketState(mainHotelMarketState);
							// @ts-ignore
							setCalendarDay({
								index: {
									day: marketStateForDay.statDay.day,
									month: marketStateForDay.statDay.month,
									year: marketStateForDay.statDay.year,
								},
							});
						}
					});
				modal.open();
			},
		};
	});


	return <Modal ref={modal.ref}
								title={marketStateForDay ? Utilities.formatDateString(marketStateForDay.statDate, 'DD MMMM YYYY') : '--'}
								width="2xlarge">
		{!!marketStateForDay && evaluator.isInitialized() && !!mainHotelMarketState && !!calendarDay &&
			<div className="inner-wrapper text-left p-4">
				<div>
					<div className="flex items-baseline flex-wrap justify-between gap-y-1 gap-x-4px-4">
						<dt className="text-sm font-medium leading-6 text-gray-500">
							Median Compset Rate
						</dt>
						<dd
							className="text-xs font-medium text-gray-300 italic">Updated: {Utilities.formatDateString(marketStateForDay!.creationDate!, 'DD MMMM HH:mm')}</dd>
						<dd className="w-full flex-none">
                    <span
											className="text-3xl font-medium tracking-tight text-gray-900">
                        {marketStateForDay!.priceMedian ? Utilities.formatPrice(marketStateForDay!.priceMedian) : '-'}
                    </span>
							<div
								className="block sm:inline-flex items-center ml-0 sm:ml-3 relative bottom-0.5">
								<span className="shrink-0 w-1.5 h-1.5 bg-gray-200 mr-1 rounded-full" />
								<span className="text-xs font-normal text-gray-400">Your difference</span>
								&nbsp;{evaluator.evaluatePriceMedian(marketStateForDay!, mainHotelMarketState).map((result, idx) =>
								<Tag key={`index_${idx}`}
										 state={result.state}>{result.value}</Tag>)}
							</div>
						</dd>
					</div>
				</div>

				<div className="mt-6">
					<div className="overflow-auto shadow ring-1 ring-black ring-opacity-5 rounded-md">
						<div className="inline-block min-w-full align-middle max-h-[500px]">
							<table className="min-w-full divide-y divide-gray-300">
								<thead className="bg-gray-50">
								<tr>
									<th scope="col"
											className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
										Hotel
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Price
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Availability
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Room Type
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Provider
									</th>
								</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">
								{marketStateForDay.hotelItems.map((item) => <MarketStateSnapshotCalendarDayTableRow
									marketStateDailyHotelSnapshot={item}
									historySnapshots={[]}
									key={`market-state-hotel-${item.hotel.id}`} />)}
								</tbody>
							</table>
						</div>
					</div>
				</div>

			</div>}
	</Modal>;
});

export default MarketStateDetailsModal;